// AccountsOverview — the three cross-account panels Ken asked for (ticket 38):
// revenue KPIs (GET /super/revenue), tenants ranked by deals in progress and
// the "falling off" list (both from GET /super/accounts). Super-admin only;
// the host page already gates on is_super_admin and both endpoints 403 otherwise.
// The two requests load independently so a slow accounts aggregation never
// hides the cheap revenue tiles.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DollarSign, TrendingUp, TrendingDown, Users, AlertTriangle, MoonStar, Briefcase, Info,
} from 'lucide-react';
import { api } from '../api';
import KpiTile from './KpiTile';
import { formatDate, fromApi } from '../kit/time';
import LongTextCell from '@/components/LongTextCell';
import { TableScroller } from '@/components/ui/table-scroller';
import { Panel, Loading, ErrorBox } from './panels';
import SpendApprovals from './SpendApprovals';

const fmt$ = (n) => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmtDate = (iso, lookbackDays) => (iso ? formatDate(iso) : `none in ${lookbackDays}d`);
const daysAgo = (iso) => Math.max(0, Math.floor((Date.now() - fromApi(iso).getTime()) / 86400000));

const PRODUCT_LABELS = { mortgage: 'Mortgage', real_estate: 'Real estate', dual_licensed: 'Dual' };

// Status colours are reserved for state and always ship with an icon + label.
const RISK = {
  dormant: { label: 'Dormant', icon: MoonStar, bg: '#FEE2E2', color: '#991B1B' },
  tapering: { label: 'Tapering', icon: TrendingDown, bg: '#FEF3C7', color: '#92400E' },
};

const TOP_N = 10;

export function RiskChip({ risk }) {
  const r = RISK[risk];
  if (!r) return null;
  const Icon = r.icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
      style={{ background: r.bg, color: r.color }} data-testid={`risk-chip-${risk}`}>
      <Icon size={12} /> {r.label}
    </span>
  );
}

const detailOf = (e, fallback) => e?.response?.data?.detail || fallback;

// One request → { data, error }. Cancels state updates after unmount.
function useGet(url, fallback) {
  const [state, setState] = useState({ data: null, error: null });
  useEffect(() => {
    let alive = true;
    api.get(url)
      .then((r) => { if (alive) setState({ data: r.data, error: null }); })
      .catch((e) => { if (alive) setState({ data: null, error: detailOf(e, fallback) }); });
    return () => { alive = false; };
  }, [url, fallback]);
  return state;
}

export default function AccountsOverview() {
  const rev = useGet('/super/revenue', 'Could not load revenue');
  const acc = useGet('/super/accounts', 'Could not load account overview');
  const [showAll, setShowAll] = useState(false);

  return (
    <div className="mb-8 space-y-5" data-testid="accounts-overview">
      {rev.error && <ErrorBox testid="revenue-error" error={rev.error} />}
      {!rev.error && !rev.data && <Loading testid="revenue-loading" label="Loading revenue…" />}
      {rev.data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiTile label="MRR" value={fmt$(rev.data.kpis.mrr)} sub="Monthly recurring" icon={DollarSign} color="#6BA82E" testid="kpi-mrr" />
          <KpiTile label="ARR" value={fmt$(rev.data.kpis.arr)} sub="Annualized" icon={TrendingUp} color="#6BA82E" testid="kpi-arr" />
          <KpiTile label="Active accounts" value={rev.data.kpis.active} sub={`${rev.data.kpis.trialing} trial · ${rev.data.kpis.past_due} past due`} icon={Users} testid="kpi-active" />
          <KpiTile label="Churn" value={`${rev.data.kpis.churn_rate}%`} sub={`${rev.data.kpis.canceled_this_month} canceled this month`} icon={TrendingDown} color="#B45309" testid="kpi-churn" />
        </div>
      )}

      <SpendApprovals />

      {acc.error && <ErrorBox testid="accounts-error" error={acc.error} />}
      {!acc.error && !acc.data && <Loading testid="accounts-loading" label="Loading account overview…" />}
      {acc.data && <AccountPanels data={acc.data} showAll={showAll} onToggle={() => setShowAll(s => !s)} />}
    </div>
  );
}

function AccountPanels({ data, showAll, onToggle }) {
  const ranked = data.accounts;
  const visible = showAll ? ranked : ranked.slice(0, TOP_N);
  const atRisk = data.at_risk;
  const lookback = data.definition.lookback_days;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1.5fr_1fr] gap-5">
      <Panel
        title="Top accounts by deals in progress"
        sub="Open mortgage and real-estate opportunities across every company tenant."
        icon={Briefcase}
        testid="top-accounts-panel"
        action={ranked.length > TOP_N && (
          <button type="button" onClick={onToggle} className="text-[12px] font-semibold text-slate-600 hover:text-slate-900" data-testid="top-accounts-toggle">
            {showAll ? `Top ${TOP_N}` : `All ${ranked.length}`}
          </button>
        )}
      >
        {ranked.length === 0 ? (
          <div className="p-6 text-sm text-slate-500 italic" data-testid="top-accounts-empty">No company tenants yet.</div>
        ) : (
          <TableScroller>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest font-bold text-slate-500 border-b border-slate-100">
                <th className="text-left px-5 py-2 w-8">#</th>
                <th className="text-left px-2 py-2">Account</th>
                <th className="text-right px-2 py-2 min-w-[4.5rem]">Mortgage</th>
                <th className="text-right px-2 py-2 min-w-[3.5rem]">RE</th>
                <th className="text-right px-2 py-2">In progress</th>
                <th className="text-right px-2 py-2">30-day activity</th>
                <th className="text-right px-5 py-2">Last login</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((a, i) => (
                <tr key={a.tenant_id} className="border-b border-slate-50 last:border-0" data-testid={`top-account-row-${a.tenant_id}`}>
                  <td className="px-5 py-2.5 text-slate-400 tabular-nums">{i + 1}</td>
                  <td className="px-2 py-2.5">
                    <LongTextCell id={`account-${a.tenant_id}`} text={a.name} lines={2} maxWidth={220} className="font-semibold text-[#070B5F]" />
                    <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                      {PRODUCT_LABELS[a.product_type] || a.product_type} · {a.subscription_tier} · {a.status}
                      {a.risk && <RiskChip risk={a.risk} />}
                    </div>
                  </td>
                  <td className="px-2 py-2.5 text-right tabular-nums text-slate-600" data-testid="mortgage-open">{a.mortgage_open}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums text-slate-600" data-testid="re-open">{a.re_open}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums font-bold" style={{ color: '#070B5F' }} data-testid="deals-in-progress">{a.deals_in_progress}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums text-slate-600" data-testid="activity-current">{a.activity_current}</td>
                  <td className="px-5 py-2.5 text-right text-slate-600 whitespace-nowrap" data-testid="last-login">{fmtDate(a.last_login_at, lookback)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </TableScroller>
        )}
      </Panel>

      <Panel
        title="Falling off"
        sub={data.definition.text}
        icon={AlertTriangle}
        testid="at-risk-panel"
      >
        {atRisk.length === 0 ? (
          <div className="p-6 text-sm text-slate-500 flex items-center gap-2" data-testid="at-risk-empty">
            <Info size={14} /> No billing-active account is dormant or tapering.
          </div>
        ) : (
          <ul className="divide-y divide-slate-50">
            {atRisk.map((a) => (
              <li key={a.tenant_id} className="px-5 py-3 flex items-start gap-3" data-testid={`at-risk-row-${a.tenant_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold" style={{ color: '#070B5F' }}>{a.name}</span>
                    <RiskChip risk={a.risk} />
                  </div>
                  <div className="text-[12px] text-slate-600 mt-0.5">{a.risk_reason}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {a.deals_in_progress} in progress · last activity {fmtDate(a.last_activity_at, lookback)}
                    {a.last_login_at ? ` · last login ${daysAgo(a.last_login_at)}d ago` : ` · no staff login in ${lookback}d`}
                  </div>
                </div>
                <Link to="/portal/super-admin/revenue" className="text-[11px] font-semibold text-slate-500 hover:text-slate-900 shrink-0">Billing</Link>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
